import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faXmark,
  faMinus,
  faMagnifyingGlass,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import states from "states-us";

const STATE_OPTIONS = states.map((s) => ({ label: s.name, value: s.abbreviation }));

const emptyFilters = { cities: [], state: "" };

/**
 * FosterFilterDrawer
 *
 * Props:
 *   isOpen          bool
 *   onClose         () => void
 *   cities          string[]   — unique city values from the data
 *   appliedFilters  { cities: string[], state: string }
 *   onApply         (filters) => void
 */
const FosterFilterDrawer = ({ isOpen, onClose, cities = [], appliedFilters, onApply }) => {
  const [pending, setPending] = useState(emptyFilters);
  const [citySearch, setCitySearch] = useState("");
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const cityRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPending(appliedFilters || emptyFilters);
      setCitySearch("");
      setCityDropdownOpen(false);
    }
  }, [isOpen]);

  // Close city dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (cityRef.current && !cityRef.current.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filteredCities = cities.filter(
    (c) =>
      c.toLowerCase().includes(citySearch.toLowerCase()) &&
      !pending.cities.includes(c)
  );

  const addCity = (city) => {
    setPending((prev) => ({ ...prev, cities: [...prev.cities, city] }));
    setCitySearch("");
    setCityDropdownOpen(false);
  };

  const removeCity = (city) => {
    setPending((prev) => ({ ...prev, cities: prev.cities.filter((c) => c !== city) }));
  };

  const clearAll = () => setPending(emptyFilters);

  const activeCount = pending.cities.length + (pending.state ? 1 : 0);

  const handleApply = () => {
    onApply(pending);
    onClose();
  };

  const handleCancel = () => {
    setPending(appliedFilters || emptyFilters);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleCancel}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col flex-1 overflow-y-auto p-8 gap-6">
          <h2 className="!font-poppins !font-bold !text-[24px] !text-gray-900 !m-0">
            Filters
          </h2>

          {/* Active filters */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="!text-sm !font-semibold text-gray-700">Active filters</span>
              {activeCount > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1.5 border border-gray-300 rounded-full px-3 py-1 !text-xs text-gray-600 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faFilter} size="xs" />
                  Clear all filters
                </button>
              )}
            </div>

            {activeCount === 0 ? (
              <p className="!text-sm text-gray-400">No active filters.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {pending.cities.map((city) => (
                  <span
                    key={city}
                    className="flex items-center gap-1.5 text-xs text-gray-700 border border-gray-300 rounded-full px-2.5 py-1"
                  >
                    <button onClick={() => removeCity(city)} className="text-gray-400 hover:text-gray-600 bg-transparent border-none outline-none">
                      <FontAwesomeIcon icon={faXmark} size="xs" />
                    </button>
                    <span><span className="font-semibold">City:</span> {city}</span>
                  </span>
                ))}
                {pending.state && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-700 border border-gray-300 rounded-full px-2.5 py-1">
                    <button
                      onClick={() => setPending((p) => ({ ...p, state: "" }))}
                      className="text-gray-400 hover:text-gray-600 bg-transparent border-none outline-none"
                    >
                      <FontAwesomeIcon icon={faXmark} size="xs" />
                    </button>
                    <span><span className="font-semibold">State:</span> {STATE_OPTIONS.find(s => s.value === pending.state)?.label || pending.state}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* City filter */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="!text-sm !font-semibold text-gray-800">City</span>
              <FontAwesomeIcon icon={faMinus} size="sm" className="text-gray-400" />
            </div>

            {/* Searchable city dropdown */}
            <div className="relative" ref={cityRef}>
              <div
                className="flex items-center gap-2 border border-gray-300 rounded-full px-3 cursor-text"
                onClick={() => setCityDropdownOpen(true)}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" size="sm" />
                <input
                  type="text"
                  placeholder="Search"
                  value={citySearch}
                  onChange={(e) => { setCitySearch(e.target.value); setCityDropdownOpen(true); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && citySearch.trim() && !pending.cities.includes(citySearch.trim())) {
                      addCity(citySearch.trim());
                    }
                  }}
                  className="flex-1 outline-none border-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
                />
                <FontAwesomeIcon icon={faChevronDown} size="xs" className="text-gray-400" />
              </div>

              {cityDropdownOpen && filteredCities.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto">
                  {filteredCities.map((city) => (
                    <button
                      key={city}
                      onClick={() => addCity(city)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 bg-transparent border-none outline-none"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected city chips */}
            {pending.cities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {pending.cities.map((city) => (
                  <span
                    key={city}
                    className="flex items-center gap-1.5 text-xs text-gray-700 border border-gray-300 rounded-full px-2.5 py-1"
                  >
                    <button onClick={() => removeCity(city)} className="text-gray-400 hover:text-gray-600 bg-transparent border-none outline-none">
                      <FontAwesomeIcon icon={faXmark} size="xs" />
                    </button>
                    {city}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* State filter */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="!text-sm !font-semibold text-gray-800">State</span>
              <FontAwesomeIcon icon={faMinus} size="sm" className="text-gray-400" />
            </div>

            <div className="relative">
              <select
                value={pending.state}
                onChange={(e) => setPending((p) => ({ ...p, state: e.target.value }))}
                className="w-full border border-gray-300 rounded-full px-4 py-2.5 text-sm text-gray-700 bg-white appearance-none outline-none focus:border-blue cursor-pointer pr-8"
              >
                <option value="">Select</option>
                {STATE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
            </div>
          </div>

          <hr className="border-gray-200" />
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-4">
          <button
            onClick={handleCancel}
            className="flex-1 py-2.5 rounded-full border border-gray-300 !text-sm text-gray-700 bg-white hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-2.5 rounded-full bg-blue !text-sm text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

export default FosterFilterDrawer;
